import { apiHelpers, extractApiData } from '@/api/core';
import {
  type RegisterRequest,
  type RegisterResponse,
  type UpdateUserRequest,
  type UpdateUserResponse,
  type DeleteUserRequest,
  type DeleteUserResponse,
  type ChangePasswordRequest,
  type ChangePasswordResponse,
  type User,
} from '@packages/types/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Query Keys
export const authKeys = {
  all: ['auth'] as const,
  users: () => [...authKeys.all, 'users'] as const,
  userList: (page: number, limit: number) => [...authKeys.users(), page, limit] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
};

// 유저 목록 조회 (pagination 지원)
export const useGetAllUsers = (page: number = 1, limit: number = 10) => {
  return useQuery({
    queryKey: authKeys.userList(page, limit),
    queryFn: async () => {
      const response = await apiHelpers.get<User[]>('auth.getAllUsers', {}, { page, limit });
      return extractApiData(response);
    },
  });
};

// 유저 프로필 조회
export const useGetUserProfile = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: async () => {
      const response = await apiHelpers.get<User>('auth.profile');
      return extractApiData(response);
    },
    enabled: options?.enabled ?? true,
  });
};

// 회원가입
export const useRegister = () => {
  return useMutation({
    mutationFn: async (payload: RegisterRequest): Promise<RegisterResponse> => {
      const response = await apiHelpers.post<RegisterResponse>('auth.register', payload);
      return extractApiData(response);
    },
  });
};

// 사용자 정보 수정
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateUserRequest): Promise<UpdateUserResponse> => {
      const response = await apiHelpers.patch<UpdateUserResponse>('auth.profile', payload);
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
      // 본인 프로필 변경이 반영되어야 하는 화면들 (Site Manager 카드 등)
      // - siteKeys.all: ['sites']
      // - siteManagerKeys.all: ['siteManagers']
      // 순환 참조 방지를 위해 prefix 문자열로 invalidate
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['siteManagers'] });
    },
  });
};

// 다른 사용자 프로필 수정 (Admin/CompanyManager)
export const useUpdateUserById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      ...payload
    }: UpdateUserRequest & { userId: number }): Promise<UpdateUserResponse> => {
      const response = await apiHelpers.patch<UpdateUserResponse>(
        'auth.updateUserProfileById',
        payload,
        { id: userId }
      );
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.users() });
      // Company Manager 등 다른 사용자 프로필 수정 후 관리 화면 목록/카드 갱신
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
};

// 비밀번호 변경
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (payload: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
      const response = await apiHelpers.post<ChangePasswordResponse>(
        'auth.changePassword',
        payload
      );
      return extractApiData(response);
    },
  });
};

// 회원 삭제
export const useDeleteUser = () => {
  return useMutation({
    mutationFn: async (payload: DeleteUserRequest): Promise<DeleteUserResponse> => {
      const response = await apiHelpers.post<DeleteUserResponse>('auth.deleteAccount', payload);
      return extractApiData(response);
    },
  });
};

